import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { isActiveOptionsList } from './constants';
import { Option, PromotionSurface } from './types';
import { axios } from '../api';
import { ActiveDatesSection } from '../component/Promotions/ActiveDatesSection';
import { ConfirmCreatePromotionModal } from '../component/Promotions/ConfirmCreatePromotionModal';
import { FreeProductSection } from '../component/Promotions/FreeProductSection';
import { PromotionBasicsCard } from '../component/Promotions/PromotionBasicsCard';
import { PromotionPageShell } from '../component/Promotions/PromotionPageShell';
import { PromotionSectionCard } from '../component/Promotions/PromotionSectionCard';
import { ToastSection } from '../component/Promotions/ToastSection';
import { TextInput } from '../component/TextInput';
import { Button } from '../component/buttons/Button';
import { formatingOptions } from '../formatingOptions';
import { useProductsByCountry } from '../hooks/useProductsByCountry';
import { usePromotionCountryCurrency } from '../hooks/usePromotionCountryCurrency';
import { BOTTOM_TOAST, generateErrorMsg } from '../utils';
import {
  buildPortalPromotionRequest,
  getStatusFromIsActiveOption,
  isValidPromotionName,
} from '../utils/promotionBuilders';

export const CreateReactivationPromotion = () => {
  const navigate = useNavigate();
  const isActiveOptions = useMemo(() => isActiveOptionsList, []);

  // basics
  const [promotionName, setPromotionName] = useState('');
  const [isActive, setIsActive] = useState(isActiveOptions[0]);

  // active dates
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [endDateError, setEndDateError] = useState('');

  // modal
  const [showConfirm, setShowConfirm] = useState(false);

  // products
  const [selectedPromoProductOption, setSelectedPromoProductOption] = useState<Option | null>(null);
  const [quantity, setQuantity] = useState('0');

  // reactivation-specific
  const [eligibleOrderNumber, setEligibleOrderNumber] = useState('0');
  const [toastTitle, setToastTitle] = useState('');
  const [toastBody, setToastBody] = useState('');

  // shared country/currency sync
  const { eligibleCountry, currency, handleCountryChange, handleCurrencyChange } =
    usePromotionCountryCurrency();

  const { freeProducts } = useProductsByCountry(eligibleCountry?.value, { includePaid: false });
  const freeProductsOptions = formatingOptions(freeProducts, { value: 'id', label: 'code' });

  const status = getStatusFromIsActiveOption(isActive);

  const isDisabled = () => {
    return (
      promotionName === '' ||
      !isValidPromotionName(promotionName) ||
      !currency ||
      !eligibleCountry ||
      Number(eligibleOrderNumber) < 1 ||
      Number(quantity) < 1 ||
      selectedPromoProductOption === null ||
      !startDate ||
      !endDate ||
      endDate <= startDate
    );
  };

  const handleCreatePromotion = () => {
    const requestBody = buildPortalPromotionRequest({
      promotionName,
      status,
      startDate,
      endDate,
      selectedPromoProductOption,
      quantity,
      currency,
      eligibleCountry,
      eligibleOrderNumber,
      toastTitle,
      toastBody,
      promotionSurface: PromotionSurface.REACTIVATION,
    });

    axios
      .post('promotions', requestBody)
      .then(() => {
        navigate('/promotions');
        toast.success(`${promotionName} Promotion has been created`, BOTTOM_TOAST);
      })
      .catch((e) => {
        setShowConfirm(false);
        toast.error(generateErrorMsg(e), BOTTOM_TOAST);
      });
  };

  return (
    <PromotionPageShell
      title="Create Plan Reactivation Promotion"
      description="This promotion will apply to plan reactivations only. When this promotion is active, the free product(s) will be displayed in the Review and Confirm page for the plan the customer is reactivating, as well as a free gift indicator in the order cards on the Home tab."
    >
      <PromotionBasicsCard
        promotionName={promotionName}
        setPromotionName={setPromotionName}
        isActive={isActive}
        isActiveOptions={isActiveOptions}
        setIsActive={setIsActive}
        currency={currency}
        eligibleCountry={eligibleCountry}
        onCurrencyChange={handleCurrencyChange}
        onCountryChange={handleCountryChange}
      />

      <PromotionSectionCard>
        <h2>Subscription order eligible for promotional product</h2>
        <p className="text-xs text-gray-500 font-normal mb-2">
          Specify which subscription order will be eligible for the promotional product. This
          counter is for subscription orders, so do not include the customer’s trial order in this
          field. For example, if you would like to add the free promotional product into the
          customer’s second subscription order (so their third S&T order when counting the trial),
          input “2” in the field below.
        </p>

        <div className="flex justify-between items-center space-x-4">
          <div className="w-1/4">
            <TextInput
              placeholder="None"
              value={eligibleOrderNumber}
              setTextChange={setEligibleOrderNumber}
            />
          </div>
          <div className="w-3/4">
            <p>subscription order</p>
          </div>
        </div>
      </PromotionSectionCard>

      <ActiveDatesSection
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        endDateError={endDateError}
        setEndDateError={setEndDateError}
        isOptional={false}
        description="The customer will only receive this promotion if they reactivate their pet's plan within this timeframe"
      />

      <FreeProductSection
        eligibleCountrySelected={!!eligibleCountry}
        options={freeProductsOptions}
        selectedOption={selectedPromoProductOption}
        onChangeOption={setSelectedPromoProductOption}
        quantity={quantity}
        setQuantity={setQuantity}
      />

      <ToastSection
        toastTitle={toastTitle}
        setToastTitle={setToastTitle}
        toastBody={toastBody}
        setToastBody={setToastBody}
      />

      <div className="flex justify-center flex-col items-center mt-2">
        <Button
          variant="primary"
          label="Create Promotion"
          onClick={() => setShowConfirm(true)}
          isDisabled={isDisabled()}
        />
      </div>

      <ConfirmCreatePromotionModal
        open={showConfirm}
        onConfirm={handleCreatePromotion}
        onCancel={() => setShowConfirm(false)}
      />
    </PromotionPageShell>
  );
};
